import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { getAzAccount, getCallerObjectId } from "../../lib/auth.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors } from "../../lib/output.js";
import { ensureAppReg } from "../../lib/app-reg.js";
import {
  createInstallerIdentity, ensureResourceGroup, grantOwnerAtSubscription, listAssignmentIds, waitForMiToken,
} from "../../lib/bootstrap-mi.js";
import { registerContainerInstance } from "../../lib/bootstrap-preflight.js";
import { kickInstaller, type GitHubAppCreds } from "../../lib/bootstrap-aci.js";
import { DEFAULT_FOUNDRY_TRACING, parseFoundryTracingMode } from "../../lib/deploy.js";
import { deriveStatusSaName, statusBlobUrl } from "../../lib/bootstrap-status.js";
import { writeBootstrapState } from "../../lib/bootstrap-state.js";
import { buildOccupiedRefusal, checkTargetRgOccupancy } from "../../lib/bootstrap-occupancy.js";
import { buildMissingCredsRefusal, buildOrgMismatchRefusal } from "../../lib/bootstrap-launch-guards.js";
import { findValidBrainApp } from "../../lib/github-app-existing.js";

const DEFAULT_RG = "rg-m8t-stack";
// PUBLIC org (m8t-labs). A regression to the private `m8t-run` org shipped a broken
// npm CLI that pulled an inaccessible installer image — guarded by a unit test.
export const DEFAULT_INSTALLER = "ghcr.io/m8t-labs/m8t-installer";
// Kept in lockstep with installer/version.json (v<version>) by a unit test, so a
// plain `m8t bootstrap launch` never pulls a stale installer image. Bump both together.
export const DEFAULT_INSTALLER_TAG = "v0.1.71";
const ACI_NAME = "m8t-installer";
const MI_NAME = "m8t-installer-mi";

export class BootstrapLaunchCommand extends M8tCommand {
  static paths = [["bootstrap", "launch"]];
  static usage = Command.Usage({
    description: "Create + authorize the installer managed identity, then kick the cloud installer.",
    details:
      "Step 2 of `m8t bootstrap` (run after `preflight`). Ensures the resource group, creates the m8t app registration (or uses --client-id), creates a user-assigned managed identity granted Owner at subscription scope, and launches the published m8t-installer image as an ACI run-to-completion job under that identity. Writes ~/.m8t/bootstrap.json for `status` and `reap`, and threads your Azure object id so the installer can grant you the platform's data-plane roles itself.",
    examples: [
      ["Launch in eastus2", "$0 bootstrap launch --location eastus2"],
      ["BYO app registration", "$0 bootstrap launch --location eastus2 --client-id <appId>"],
      ["Pin a specific installer tag", "$0 bootstrap launch --location eastus2 --installer-tag v0.1.42"],
      ["Override the full installer image ref", "$0 bootstrap launch --location eastus2 --installer-image ghcr.io/m8t-labs/m8t-installer:v0.1.42"],
      ["Share a support contact", "$0 bootstrap launch --location eastus2 --contact-email you@example.com --company 'Acme'"],
    ],
  });

  location = Option.String("--location");
  resourceGroup = Option.String("--resource-group");
  clientId = Option.String("--client-id");
  subscription = Option.String("--subscription");
  installerTag = Option.String("--installer-tag");
  // Full image ref override (registry + repo + tag) — an escape hatch when the
  // default org/tag is wrong for the current CLI (e.g. a stale published build).
  // Wins over --installer-tag / the pinned default.
  installerImage = Option.String("--installer-image");
  gatewayImageRef = Option.String("--gateway-image-ref");
  githubAppCreds = Option.String("--github-app-creds");
  contactEmail = Option.String("--contact-email", { description: "Share a contact email with m8t support. Not sent unless you pass it." });
  company = Option.String("--company", { description: "Share your company name with m8t support. Not sent unless you pass it." });
  // Value-carrying on purpose: a bare --force would be cargo-culted into
  // runbooks and harness prompts and erode the protection, whereas a faithful
  // paste can never accidentally carry the victim group's name. It AUTHORIZES
  // the target; --resource-group is what CHOOSES it.
  reinstallInto = Option.String("--reinstall-into", { description: "Consent to installing into --resource-group even though it already holds resources. Must match the target group's name." });
  org = Option.String("--org", { description: "Assert the GitHub App on disk is installed on this org; refuses on a mismatch." });
  noBrains = Option.Boolean("--no-brains", false, { description: "Install without brain-backed workers. For test and CI rigs; the supported install creates brains." });
  // The opt-out TELEMETRY.md names. Without it there is no way to decline at
  // install time — the ACI env is built entirely from these options, so a
  // founder setting FOUNDRY_TRACING in their own shell reaches nothing. A
  // privacy notice that documents a control has to be a control that exists.
  foundryTracing = Option.String("--foundry-tracing", { description: "Where agent traces go: project (default, your own App Insights) | account (legacy shared) | skip (no tracing — you lose the record of what your agents did)." });

  async executeCommand(): Promise<number> {
    const location = typeof this.location === "string" ? this.location : undefined;
    if (!location) {
      throw new LocalCliError({ code: "USAGE", message: "--location is required.", hint: "Example: m8t bootstrap launch --location eastus2" });
    }
    const resourceGroupOpt = typeof this.resourceGroup === "string" ? this.resourceGroup : undefined;
    const resourceGroup = resourceGroupOpt ?? DEFAULT_RG;
    const clientIdOpt = typeof this.clientId === "string" ? this.clientId : undefined;
    const installerTag = (typeof this.installerTag === "string" ? this.installerTag : undefined) ?? DEFAULT_INSTALLER_TAG;
    const installerImageOverride = typeof this.installerImage === "string" ? this.installerImage : undefined;
    const installerImage = installerImageOverride ?? `${DEFAULT_INSTALLER}:${installerTag}`;
    const gatewayImageRef = typeof this.gatewayImageRef === "string" ? this.gatewayImageRef : undefined;
    // Parse here, not at the kick: a typo'd mode is a usage error, and refusing
    // after the app registration, identity and resource group already exist
    // leaves the founder to clean up an install that was never going to start.
    const foundryTracing =
      parseFoundryTracingMode(typeof this.foundryTracing === "string" ? this.foundryTracing : undefined)
      ?? DEFAULT_FOUNDRY_TRACING;

    const account = await getAzAccount();
    const subscriptionId = (typeof this.subscription === "string" ? this.subscription : undefined) ?? account.subscriptionId;
    const out = (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`);

    // ── Occupancy guard ────────────────────────────────────────────────────
    // First statement that touches the target, ahead of EVERY mutation
    // (provider registration, app registration, resource group). Installing
    // into a group that already holds resources once overwrote a live
    // deployment's identity; refusing by default is the containment.
    const reinstallInto = typeof this.reinstallInto === "string" ? this.reinstallInto : undefined;
    if (reinstallInto !== undefined && reinstallInto.toLowerCase() !== resourceGroup.toLowerCase()) {
      // Checked before the ARM call: a mismatch needs no network to reject.
      // Pre-observation path: no occupancy read has happened yet, so the
      // message must not hand over a pre-consented command — only route the
      // founder to see the group first (the hint below does that).
      const targetExplanation = resourceGroupOpt === undefined
        ? `you did not pass --resource-group, so the target is the default '${DEFAULT_RG}'`
        : `the target resource group is '${resourceGroup}'`;
      throw new LocalCliError({
        code: "BOOTSTRAP_REINSTALL_TARGET_MISMATCH",
        message: `--reinstall-into '${reinstallInto}' does not match — ${targetExplanation}.`,
        hint: `--reinstall-into authorizes the target; it does not choose it. Re-run with --resource-group ${reinstallInto} on its own first — the guard will show you what that group holds.`,
      });
    }
    const occupancy = await checkTargetRgOccupancy({ resourceGroup, subscriptionId });
    if (occupancy.state === "occupied" && reinstallInto === undefined) {
      this.context.stderr.write(buildOccupiedRefusal({ resourceGroup, subscriptionId, location, occupancy }));
      throw new LocalCliError({
        code: "BOOTSTRAP_TARGET_OCCUPIED",
        message: `Resource group '${resourceGroup}' already holds ${String(occupancy.total)} resources.`,
      });
    }
    // Structural stop for the guard's own live proof: returns HERE, so the
    // shared app registration below is unreachable rather than un-raced.
    // Env-gated so it has no --help surface; honored only for exactly "1".
    if (this.context.env.M8T_HALT_AFTER_OCCUPANCY === "1") {
      this.context.stdout.write(`halt: occupancy check passed (${occupancy.state}) for ${resourceGroup}\n`);
      return 0;
    }

    // Read the GitHub App creds written by `m8t brain app-create`.
    //
    // This runs BEFORE the app registration and the installer identity on
    // purpose: the installer cannot create brains without these, and failing
    // after the identity exists strands an Owner-at-subscription-scope grant
    // that nothing is left holding the state to clean up.
    const credsPath =
      typeof this.githubAppCreds === "string"
        ? this.githubAppCreds
        : path.join(os.homedir(), ".m8t", "github-app.json");
    const skipBrains = this.noBrains === true;
    const requestedOrg = typeof this.org === "string" ? this.org : undefined;
    let githubApp: GitHubAppCreds | undefined;

    if (skipBrains) {
      out("installing without brain-backed workers; no GitHub App credentials will be used");
    } else if (!fs.existsSync(credsPath)) {
      this.context.stderr.write(buildMissingCredsRefusal(credsPath));
      throw new LocalCliError({
        code: "GITHUB_APP_CREDS_MISSING",
        message: `No GitHub App credentials at ${credsPath}.`,
        hint: "Run `m8t brain app-create --org <org>` first.",
      });
    } else {
      let parsed: { appId: string; slug: string; org: string; installationId: string; pemPath: string };
      try {
        parsed = JSON.parse(fs.readFileSync(credsPath, "utf8")) as typeof parsed;
      } catch {
        throw new LocalCliError({
          code: "GITHUB_APP_CREDS_INVALID",
          message: `Could not parse GitHub App creds at ${credsPath}.`,
          hint: "Run `m8t brain app-create --org <org>` to regenerate.",
        });
      }

      // Deterministic and local. A stale creds file from an earlier install into
      // a different org would otherwise put this founder's brains somewhere they
      // did not choose, silently.
      if (requestedOrg !== undefined && requestedOrg.toLowerCase() !== parsed.org.toLowerCase()) {
        this.context.stderr.write(buildOrgMismatchRefusal({ requested: requestedOrg, recorded: parsed.org }));
        throw new LocalCliError({
          code: "GITHUB_APP_ORG_MISMATCH",
          message: `GitHub App creds are for '${parsed.org}', but --org named '${requestedOrg}'.`,
          hint: `Run \`m8t brain app-create --org ${requestedOrg}\` first.`,
        });
      }

      let pem: string;
      try {
        pem = fs.readFileSync(parsed.pemPath, "utf8");
      } catch {
        throw new LocalCliError({
          code: "GITHUB_APP_PEM_MISSING",
          message: `GitHub App PEM not found at ${parsed.pemPath}.`,
          hint: "Run `m8t brain app-create --org <org>` to regenerate.",
        });
      }
      githubApp = {
        appId: parsed.appId,
        slug: parsed.slug,
        org: parsed.org,
        installationId: parsed.installationId,
        pemB64: Buffer.from(pem).toString("base64"),
      };

      // Advisory ONLY - deliberately not a refusal. findValidBrainApp collapses
      // every failure to null, including an unreachable api.github.com, so
      // refusing on it would stop a founder whose credentials are fine because
      // their network blipped. A gate may fail closed only on a signal that
      // cannot fail transiently; this one can. If the App really is gone, the
      // installer fails honestly a few minutes later.
      const stillInstalled = await findValidBrainApp(credsPath, parsed.org);
      if (stillInstalled === null) {
        out(`warning: could not confirm the GitHub App is still installed on ${parsed.org} - continuing`);
      }

      out(`threading GitHub App creds for org ${parsed.org} (app ${parsed.appId})…`);
    }

    // G2: substrate ready (defensive — preflight already registered it).
    out("registering Microsoft.ContainerInstance…");
    await registerContainerInstance(subscriptionId);

    // App reg (programmatic admin-consent under the signed-in admin; or BYO).
    out("resolving the m8t app registration…");
    const appReg = await ensureAppReg({ tenantId: account.tenantId, clientId: clientIdOpt });

    out(`ensuring resource group ${resourceGroup}…`);
    await ensureResourceGroup({ name: resourceGroup, location, subscriptionId });

    out("creating the installer managed identity…");
    const mi = await createInstallerIdentity({ name: MI_NAME, resourceGroup, location, subscriptionId });

    out("granting Owner at subscription scope…");
    await grantOwnerAtSubscription({ principalId: mi.principalId, subscriptionId });
    const roleAssignmentIds = await listAssignmentIds({ principalId: mi.principalId, subscriptionId });

    // Persist state NOW — after the MI + its Owner@sub role exist, but BEFORE the
    // ACI create that can fail (e.g. an inaccessible installer image). Otherwise a
    // launch that dies here leaves the privileged Owner@sub grant orphaned with no
    // local state, so `m8t bootstrap reap --force` finds nothing to clean up.
    const saName = deriveStatusSaName(resourceGroup, subscriptionId);
    await writeBootstrapState({
      subscriptionId, resourceGroup, location,
      aciName: ACI_NAME, miName: MI_NAME, miClientId: mi.clientId, miPrincipalId: mi.principalId,
      roleAssignmentIds, appRegClientId: appReg.clientId,
      statusSaName: saName, statusBlobUrl: statusBlobUrl(saName), installerTag,
    });

    out("waiting for the installer identity to be usable (role propagation)…");
    const miReady = await waitForMiToken({ clientId: mi.clientId, subscriptionId, onProgress: out });
    if (!miReady) out("identity not confirmed yet — launching anyway (the installer retries its own login)");

    // Who the founder IS — the one ingredient the installer has never had, and
    // the reason granting them their own platform's data-plane roles used to be a
    // command they typed afterwards. Threaded into the ACI env so step 3b can
    // grant them alongside the MI's existing self-grant, EARLY, while there is
    // still an install to grant against.
    //
    // Best-effort on purpose. This is decoded locally from an ARM token we can
    // already mint (nothing here reaches Graph), so a failure means something is
    // wrong with the session rather than with this feature — and refusing to
    // launch over it would trade a working install for a convenience. Absent, the
    // installer skips the grant and the founder runs `m8t prereqs --fix`, exactly
    // as they do today.
    let founderObjectId: string | undefined;
    try {
      founderObjectId = await getCallerObjectId();
    } catch (e) {
      out(`warning: could not determine your Azure object id (${(e as Error).message}) — the install will not grant you data-plane access; run 'm8t prereqs --fix' afterwards`);
    }

    out("launching the cloud installer (ACI)…");
    await kickInstaller({
      aciName: ACI_NAME, resourceGroup, location, subscriptionId,
      miResourceId: mi.resourceId, miClientId: mi.clientId, appRegClientId: appReg.clientId,
      image: installerImage, gatewayImageRef, foundryTracing,
      githubApp,
      ...(founderObjectId ? { founderObjectId } : {}),
      ...(skipBrains ? { skipBrains: true } : {}),
      // Opt-in only. The signed-in UPN was previously seeded here automatically;
      // an installation is identified by its random instance id, and contact
      // details are sent only when the operator asks for them.
      ...(typeof this.contactEmail === "string" ? { enrollContactEmail: this.contactEmail } : {}),
      ...(typeof this.company === "string" ? { enrollCompany: this.company } : {}),
      ...(process.env.M8T_UPDATE_CHANNEL_URL ? { updateChannelUrl: process.env.M8T_UPDATE_CHANNEL_URL } : {}),
    });

    this.context.stdout.write(
      `${colors.success("✓")} installer launched in ${colors.field(resourceGroup)} (${location}).\n` +
      `  ${colors.hint("next:")} m8t bootstrap status --watch   ${colors.dim("# watch the install to done")}\n`,
    );
    return 0;
  }
}
